import {
	Component,
	OnInit,
	Input,
	ViewChild,
	Output,
	ViewEncapsulation,
	EventEmitter,
	OnDestroy,
	AfterViewInit
} from "@angular/core";
import { IdService } from "../shared/services/id.service";

import { isMobile } from "../shared/utils/mobile";

export class SortLocale {
	sortArrowUpLabel = "Move item up";
	sortArrowDownLabel = "Move item down";
}

@Component({
	selector: "rbc-sort",
	template: `
		<ul
			#container
			[id]="id"
			[ngStyle]="{ height: height }"
			[ngClass]="{
				'rbc-sort-active': activeIndex !== null
			}"
			class="rbc-sort">
			<li
				*ngFor="let item of reorderedItems; index as index"
				[attr.key]="id + (item.id || index)"
				[id]= "item !== '' ? id + item.id : null"
				[ngClass]="{
					'active': newIndex === index + difference,
					'ghost': item === ''
				}"
				class="rbc-sort-item"
				(mouseleave)= "(activeIndex !== null) ? set() : null">
				<div *ngIf="item !== ''" class="rbc-sort-item-text">
					<rbc-checkbox
						*ngIf="checkable"
						[id]="id +item.id+'Checkbox'"
						[disabled]="item.static"
						[(ngModel)]="item.checked"
						[checked]="item.checked"
						[ariaLabel]="'Select or deselect ' + id + item.id + 'Checkbox'"
					></rbc-checkbox>
					{{ item.name || item }}
				</div>
				<div *ngIf="item !== ''" class="rbc-sort-item-button-wrap">
					<button
						[id]="id + item.id+ 'SortUp'"
						[disabled]="checkStatic(index, 'up')"
						[attr.aria-label]="locale.sortArrowUpLabel"
						class="rbc-sort-item-button rbc-sort-item-button-up"
						(click)="moveUp(index, item.id)"
						(keydown)="setOnKeydown($event, item.id)">
						<rbc-icon icon="arrow-up" [type]="'solid'"></rbc-icon>
					</button>
					<button
						[id]="id + item.id+ 'SortDown'"
						[disabled]="checkStatic(index, 'down')"
						[attr.aria-label]="locale.sortArrowDownLabel"
						class="rbc-sort-item-button rbc-sort-item-button-down"
						(click)="moveDown(index, item.id)"
						(keydown)="setOnKeydown($event, item.id)">
						<rbc-icon icon="arrow-down" [type]="'solid'"></rbc-icon>
					</button>
				</div>
			</li>

			<!-- Edge Positioned item (Occurs when first item in the list is being moved down) -->
			<li
				*ngIf="edgePositioned && oldIndex === 0 && newIndex - 1 > -1"
				class="rbc-sort-item edge-item">
				<div
					*ngIf="items[newIndex - 1] !== ''"
					class="rbc-sort-item-text">
					<rbc-checkbox
						*ngIf="checkable"
						[disabled]="items[newIndex - 1].static"
						[(ngModel)]="items[newIndex - 1].checked"
						[checked]="items[newIndex - 1].checked"
						[ariaLabel]="'Select or deselect ' + id + 'ItemEdge'">
					</rbc-checkbox>
					{{ items[newIndex - 1].name || items[newIndex - 1] }}
				</div>
			</li>
		</ul>
	`,
	styles: [],
	encapsulation: ViewEncapsulation.None
})
export class SortComponent implements OnDestroy, AfterViewInit {
	@Input() checkable: boolean;
	@Input() options;
	@Input() locale = {
		sortArrowUpLabel: "Move item up",
		sortArrowDownLabel: "Move item down"
	};

	@Input() id: string;
	activeId = null;
	activeIndex = null;
	newIndex = null;
	oldIndex = null;
	difference = 0;
	items = [];
	reorderedItems: any = [];
	timeout = null;
	height = "auto";
	edgePositioned = false;

	@Output() sortUpdated = new EventEmitter();
	@ViewChild("container", { static: false }) $container;

	constructor(private idService: IdService) {
		this.id = idService.generate();
	}

	ngAfterViewInit() {
		// create a copy of the items for display only
		if (this.options) {
			// add an id for use in arrow buttons refs
			this.items = this.options.map((item, index) => {
				return {
					...item,
					id: `item${index}`
				};
			});
			// copys the array not the reference
			this.reorderedItems = [...this.items];
		}
		setTimeout(() => {
			this.height = this.$container.nativeElement.getBoundingClientRect().height + "px";
		}, 100);

	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
		document.removeEventListener("touchend", this.setOnClick);
	}



	checkStatic(index, direction) {
		// If static is true return true
		if (
			typeof this.reorderedItems[index] !== "undefined" &&
			typeof this.reorderedItems[index].static !== "undefined" &&
			!!this.reorderedItems[index].static
		) {
			return true;
		}
		// If up button has been pressed
		if (direction === "up") {
			if (
				index + this.difference === 0 &&
				this.items.length !== this.difference - 1
			) {
				return true;
			} else if (
				this.reorderedItems[index - 1] &&
				typeof this.reorderedItems[index - 1].static !== "undefined" &&
				this.reorderedItems[index - 1].static === true
			) {
				return true;
			} else {
				return false;
			}
		}
		// If down button has been pressed
		if (direction === "down") {
			if (
				index === this.reorderedItems.length ||
				index + this.difference + 1 === this.items.length
			) {
				return true;
			} else if (
				typeof this.reorderedItems[index + 1] !== "undefined" &&
				typeof this.reorderedItems[index + 1].static !== "undefined" &&
				!!this.reorderedItems[index + 1].static
			) {
				return true;
			} else {
				return false;
			}
		}
	}

	setIndex(index, count) {
		// Index of item being moved and count is -1 for up and 1 for down
		// save the original index before moving array items around
		// as reference for the original items vs the reordered items
		if (this.oldIndex === null) {
			this.oldIndex = index;

			// If the element being moved is at the beginning or end of the list make it edge posititon
			if (
				this.oldIndex === 0 ||
				this.oldIndex + 1 === this.items.length
			) {
				this.edgePositioned = true;
			}

			if (isMobile()) {
				// set a listener to set the new ordered list when clicked off
				document.addEventListener("touchend", this.setOnClick);
			}
		}

		// set new indexes to include the array difference
		this.activeIndex = this.oldIndex + this.difference;
		this.newIndex = this.activeIndex + count;
	}

	move() {
		// grab the selected item in the array
		let removed = this.items.splice(this.newIndex, 1)[0];
		// and move it to the new position
		this.items.splice(this.activeIndex, 0, removed);


		// if we're going up
		if (this.difference < 0) {
			// grab and add extra array items with the newly arranged items array
			const extras = this.reorderedItems.splice(0, 0 - this.difference - 1);
			this.reorderedItems = [...extras, ...this.items];

			// add one more array item to push list items down
			// and keep active item in same position
			this.reorderedItems.splice(0, 0, "");

			// if we're going down
		} else if (this.difference > 0) {
			this.reorderedItems = [...this.items];

			// shave off the difference from the top of the displayed array
			for (let i = 0; i < this.difference; i++) {
				this.reorderedItems.shift();
			}
		} else {
			// update reordered items to match
			this.reorderedItems = [...this.items];
		}

		if (isMobile()) {
			clearTimeout(this.timeout);

			// set the new sorted list if the user has not clicked off within 2s
			this.timeout = setTimeout(() => {
				this.set();
			}, 2000);
		}
	}

	moveUp(index, id) {
		this.setIndex(index, -1);
		this.activeId = id;

		// if (this.newIndex + 1 > 0) {
		this.difference = this.difference - 1;

		this.move();

		setTimeout(() => {
			document.getElementById(this.id + this.activeId + "SortUp").focus();
		});
		// }
	}

	moveDown(index, id) {
		this.setIndex(index, 1);
		this.activeId = id;

		// if (this.newIndex < this.items.length) {
		this.difference = this.difference + 1;

		this.move();
		// }
	}

	setFocus(dir) {
		// set the focus state to be on an arrow button dependant on the active item's id
		// if the item is at the edge, then focus its adjacent arrow direction
		setTimeout(() => {
			let refArrow = document.getElementById(this.id + this.activeId + "Sort" + dir);
			if (refArrow["disabled"] && dir === "Up") {
				document.getElementById(this.id + this.activeId + "SortDown").focus();
			} else if (refArrow["disabled"] && dir === "Down") {
				document.getElementById(this.id + this.activeId + "SortUp").focus();
			} else {
				refArrow.focus();
			}
		});
	}

	sortSetTimeout() {
		clearTimeout(this.timeout);

		// set the new sorted list if the user has not clicked off or interacted within 2s
		this.timeout = setTimeout(() => {
			this.set();
		}, 2000);
	}

	set() {
		// replace the displayed array with the real rearranged results
		this.reorderedItems = [...this.items];

		// check which direction the item is moving
		let arrowDir = this.getArrowDirection();
		// let the parent know what has happened today
		this.sortUpdated.emit({
			previousIndex: this.oldIndex,
			newIndex: this.newIndex,
			data: this.items
		});

		// reset everything to have a fresh start
		this.activeIndex = null;
		this.oldIndex = null;
		this.newIndex = null;
		this.difference = 0;
		this.timeout = null;

		document.removeEventListener("touchend", this.setOnClick);
		this.setFocus(arrowDir);
	}

	setOnClick(e) {
		// if the click is not in the sorting container than sort the set - This event listener will be attached to the document
		const container = this.$container;

		if (!(container === e.target || container.contains(e.target))) {
			this.set();
		}
	}

	setOnKeydown(e, id) {
		const key = e.key.toLowerCase();

		// on enter keydown, if user is inactive for 2s, then show the new ordered list
		/* istanbul ignore else */
		if (key.indexOf("enter") !== -1) {
			this.activeId = id;
			this.sortSetTimeout();
		}
	}

	getArrowDirection() {
		// return string of direction for arrow $refs
		if (this.difference < 0 || this.activeIndex > this.newIndex) {
			return "Up";
		} else {
			return "Down";
		}
	}

	newHeight() {
		this.height = this.$container.nativeElement.getBoundingClientRect().height + "px";
	}
}
